import { Box, Autocomplete, Grid, FormControl, TextField, MenuItem, Typography, useMediaQuery, useTheme, Button, Stack, SvgIcon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, InputAdornment, Checkbox, Link, FormControlLabel, FormGroup } from '@mui/material';
import { AutoshipmentTemplatesFilterOptions, Item, OrderType } from '../../../../app/services/api/orderManagementClient';
import { ItemsFilterOptions, client } from '../../../../app/services/api/orderManagementClient';
import { SearchSm } from '../../../../assets';
import CustomPagination from '../../../../components/forms/Pagination';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';
import PageArea from '../../../../components/shared/PageArea';
import PageTitle from '../../../../components/shared/PageTitle';
import { UserContext } from '../../../../components/shared/useUser';
import CustomBreadCrumbs from '../../../../components/ui/CustomBreadCrumbs';
import LoadingWheel from '../../../../components/ui/LoadingWheel';
import { OrderTypeTranslationMap, OrderType as OrderTypeEnum } from '../../../../enums/OrderType';
import { ChangeEvent, SyntheticEvent, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import { Save01 } from '../../../../assets';
import { Download, Upload } from '@mui/icons-material';
import { Section } from '../../../../components/ui/Section';
import useLogError from '../../../../hooks/useLogError';
import { AutoshipTemplate } from '../../../../app/services/api/orderManagementClient';
import { Result } from '../../../../app/services/api/orderManagementClient';
import { IdNameModel } from '../../../../app/services/api/orderManagementClient';
import { base64ToBlob } from '../../../../utils';
import AutoshipUploadFileModal from '../../modals/AutoshipUploadFileModal';
import { ActionConfirmationDialog } from '../../../../components/shared/dialogs/ActionConfirmationDialog';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const CreateOrEditAutoshipmentTemplate = () => {
  const theme = useTheme();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { hasPermissionTo, isAdmin, selectedStore }: any = useContext(UserContext);
  const hasWritePermissions = hasPermissionTo(['AutoshipmentWrite']) || isAdmin();

  const { t } = useTranslation('autoshipment');
  const [items, setItems] = useState<Item[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<string>('Name');

  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.sm}px)`);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const pageReset = useRef(false);
  const [orderTypes, setOrderTypes] = useState<OrderType[]>([]);
  const [autoshipReasons, setAutoshipReasons] = useState<IdNameModel[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const navigate = useNavigate();
  const [selectAll, setSelectAll] = useState(false);
  const { logError } = useLogError();
  const { id } = useParams<{ id: string | undefined }>();
  const parsedId = id ? parseInt(id, 10) : null;
  const isEditMode = !!(id && id !== '0');
  const [openUploadItemsDialog, setOpenUploadItemsDialog] = useState(false);
  const [showOrderTypeWarning, setShowOrderTypeWarning] = useState(false);
  const [pendingOrderType, setPendingOrderType] = useState<number | null>(null);
  const [selectedReasons, setSelectedReasons] = useState<IdNameModel[]>([]);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string>('');

  const [formValues, setFormValues] = useState({
    id: null as number | null,
    name: '',
    orderTypeId: -1,
    status: null as number | null,
    createDate: null as string | null,
    selectedItems: [] as number[],
    existingTemplates: [] as string[],
    hasPublishedSchedules: false as boolean,
  });

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const handleFormChange = (field: keyof typeof formValues) => 
    (event: React.ChangeEvent<HTMLInputElement | { value: unknown }>) => {
      setFormValues(prev => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);

        const [orderTypesRes, autoshipReasonsRes] = await Promise.all([
          client.getOrderTypes(selectedStore?.storeNumber),
          client.getAutoshipReasons(selectedStore?.storeNumber),
        ]);

        setOrderTypes(orderTypesRes.data?.filter(x => x.id != 12) ?? []);
        setAutoshipReasons(autoshipReasonsRes?.data ?? []);

        const defaultOrderType = orderTypesRes.data.length > 0 ? orderTypesRes.data[0].id : -1;
        setFormValues((prev) => ({ ...prev, orderTypeId: defaultOrderType }));
        
        // Optionally fetch template if in edit mode
        if (isEditMode && id) {
          const templateResult = await client.getAutoshipTemplateById(id, selectedStore?.storeNumber);
          if (templateResult?.success && templateResult.data) {
            setFormValues(templateResult.data);
            setSelectedReasons(templateResult.data.selectedReasons ?? []);

            if(templateResult.data.selectAll)
            {
              setSelectAll(true);
            }

            // If no write permissions, only fetch and display selected items
            if ((!hasWritePermissions && templateResult.data.selectedItems && templateResult.data.selectedItems.length > 0) || templateResult.data.hasPublishedSchedules) {
              const itemResult = await fetchItems({
                item: '',
                orderType: templateResult.data.orderTypeId,
                storeNumber: selectedStore?.storeNumber,
                categoryId: -1,
                supplier: '',
                pageNumber: 0,
                pageSize: templateResult.data.selectedItems.length,
                sort: 'Name',
                direction: 'asc',
                itemIds: templateResult.data.selectedItems,
              });
              
              const filteredItems = itemResult.filter(item => 
                templateResult.data.selectedItems.includes(item.id),
              );
              setItems(filteredItems);
              setTotalPages(1);
            } else if (hasWritePermissions) {
              await fetchItems({
                item: '',
                orderType: templateResult.data.orderTypeId,
                storeNumber: selectedStore?.storeNumber,
                categoryId: -1,
                supplier: '',
                pageNumber: page,
                pageSize: pageSize,
                sort: 'Name',
                direction: 'asc',
              });
            }
          } else {
            logError(templateResult?.message ?? 'Failed to fetch template');
          }
        } else if (hasWritePermissions) {
          await fetchExistingTemplateNames();
          await fetchItems({
            item: '',
            orderType: defaultOrderType,
            storeNumber: selectedStore?.storeNumber,
            categoryId: -1,
            supplier: '',
            pageNumber: page,
            pageSize: pageSize,
            sort: 'Name',
            direction: 'asc',
          });
        }

      } catch (error) {
        logError(error);
        handleSnackbarOpen(t('errorLoadingData') + ' ' + error, 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [selectedStore?.storeNumber]);

  const fetchItemsOnPagination = async () => {
    if (!isLoading && formValues.orderTypeId !== -1 && !pageReset.current) {
      setIsLoading(true);

      await fetchItems({
        item: productSearch,
        orderType: formValues.orderTypeId,
        storeNumber: selectedStore?.storeNumber,
        categoryId: -1,
        pageNumber: page,
        pageSize: pageSize,
        sort: orderBy,
        direction: order,
        supplier: undefined,
      });

      setIsLoading(false);
    }

    pageReset.current = false;
  };

  useEffect(() => {
    fetchItemsOnPagination()
      .catch((error) => {
        handleSnackbarOpen(t('errorLoadingData') + ' ' + error, 'error');
      });
  }, [page]);

  const fetchItems = async (filters: ItemsFilterOptions): Promise<Item[]> => {
    try {
      const itemsResponse = await client.getItems(filters);
      setItems(itemsResponse.data.items);
      setTotalPages(itemsResponse.data.totalPages);
      if (page !== itemsResponse.data.pageNumber) {
        pageReset.current = true;
        setPage(itemsResponse.data.pageNumber);
      }

      setIsLoading(false);
      return itemsResponse.data.items;
    } catch (error) {
      setIsLoading(false);
      handleSnackbarOpen(t('errorLoadingData') + ' ' + error, 'error');
      return [];
    }
  };

  const fetchExistingTemplateNames = async () => {
    try {
      const templateNamesResult = await client.getAutoshipTemplateNames(selectedStore?.storeNumber);
      const templateNames = templateNamesResult.data || [];
      setFormValues(prev => ({
        ...prev,
        existingTemplates: templateNames,
      }));

    } catch (error) {
      logError(error);
    }
  };

  const handleSnackbarOpen = (message: string, severity: 'error' | 'warning' | 'info' | 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const downloadItemsCsv = async () => {
    setIsLoading(true);
    try {
      const result = await client.downloadItemsCsv(selectedStore?.storeNumber, formValues.orderTypeId?.toString());
      if (result && result.success) {
        const blob = base64ToBlob(result.data.fileData, 'text/csv');
        const downloadLink = document.createElement('a');
        downloadLink.href = URL.createObjectURL(blob);
        downloadLink.download = `${orderTypes.find(x => x.id == formValues?.orderTypeId)?.name}-items.csv`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }
    } catch (error) {
      handleSnackbarOpen(t('downloadFailed') + ' ' + error, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const getFilterOptions = (resetPage: boolean) => {
    const filterOptions: ItemsFilterOptions = {
      storeNumber: selectedStore?.storeNumber,
      item: productSearch,
      orderType: formValues.orderTypeId,
      categoryId: -1,
      pageNumber: resetPage ? 0 : page,
      pageSize: pageSize,
      sort: orderBy,
      direction: order,
      supplier: undefined,
    };
    return filterOptions;
  };

  const handleSearch = async () => {
    setIsLoading(true);
    await fetchItems(getFilterOptions(true));
    setIsLoading(false);
  };

  const handleItemChange = (itemId: number, isChecked: boolean) => {
    if(!hasWritePermissions) return;

    if (isChecked) {
      const selecteditem = items?.find(row => row.id === itemId);
      if (selecteditem) {
        if (!formValues.selectedItems?.some(e => e == itemId)) {
          setFormValues(prev => ({
            ...prev,
            selectedItems: [...prev.selectedItems, itemId!],
          }));
        }
      }
    } else {
      setFormValues(prev => ({
        ...prev,
        selectedItems: prev.selectedItems.filter(i => i !== itemId),
      }));
    }
    setSelectAll(false);
  };

  const handleSelectAllChange = (event: ChangeEvent<HTMLInputElement>) => {
    if(!hasWritePermissions) return;
    const checked = event.target.checked;
    if(!checked) {
      setFormValues(prev => ({
        ...prev,
        selectedItems: [],
      }));
    }
    setSelectAll(checked);
  };

  const onSave = async () => {
    if(!hasWritePermissions) return;
    setIsLoading(true);
    
    try {
      const model: AutoshipTemplate = {
        id: parsedId,
        name: formValues.name,
        orderTypeId: formValues.orderTypeId,
        selectedReasons: selectedReasons.map(x => x.id),
        selectedItems: formValues.selectedItems,
        selectAll: selectAll,
      };

      const result = await client.createOrUpdateAutoshipTemplate(model, selectedStore?.storeNumber);
      
      if (result.success && result.data > 0) {
        const successMessage = isEditMode 
          ? t('templateUpdatedSuccessfully') 
          : t('templateCreatedSuccessfully');
        
        handleSnackbarOpen(successMessage, 'success');
        
        // If creating new template, navigate to edit mode with the new ID
        if (!isEditMode) {
          navigate(`/manage-autoshipments/template/${result.data}`, { replace: true });
          setFormValues(prev => ({ ...prev, status: 1 }));
        }
      } else {
        handleSnackbarOpen(t('failedToSaveTemplate'), 'error');
      }
    } catch (error) {
      logError(error);
      handleSnackbarOpen(t('errorSavingTemplate') + ' ' + error, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const validateItems = async (file: File): Promise<Result<any>> => {
    try {
      const reader = new FileReader();
      
      return new Promise((resolve) => {
        reader.onload = async (e) => {
          try {
            const base64 = e.target?.result?.toString().split(',')[1] ?? '';
            const fileRequest = { fileData: base64, fileName: file.name };

            const response = await client.uploadTemplateItemsCsv(selectedStore?.storeNumber, formValues?.orderTypeId, fileRequest);
            
            if (response?.success) {
              if(response.data.length === 0) {
                const noValidItemsMsg = t('noItemsFoundInFile');
                setUploadErrorMessage(noValidItemsMsg);
                resolve({ success: false, message: noValidItemsMsg, data: [] });
                return;
              }
              
              handleSnackbarOpen(t('successfullyUploaded'), 'success');
              setUploadErrorMessage('');
              resolve(response);
            } else {
              const errorMsg = t('invalidItemsUploaded');
              setUploadErrorMessage(errorMsg);
              resolve({ success: false, message: errorMsg, data: response?.data || [] });
            }
          } catch (error) {
            const errorMsg = t('fileUploadError') + ' ' + error;
            setUploadErrorMessage(errorMsg);
            resolve({ success: false, message: errorMsg, data: [] });
          }
        };
        
        reader.onerror = () => {
          const errorMsg = t('fileReadError');
          setUploadErrorMessage(errorMsg);
          resolve({ success: false, message: errorMsg, data: [] });
        };
        
        setUploadErrorMessage('');
        reader.readAsDataURL(file);
      });
    } catch (error) {
      const errorMsg = t('fileUploadError') + ' ' + error;
      setUploadErrorMessage(errorMsg);
      handleSnackbarOpen(errorMsg, 'error');
      return { success: false, message: errorMsg, data: [] };
    }
  };

  const handleCloseDialog = () => {
    setOpenUploadItemsDialog(false);
  };

  const onConfirmBulkUpload = (validItems: Item[]) => {
    const validItemIds = validItems.map(item => item.id);
    setFormValues(prev => ({
      ...prev,
      selectedItems: validItemIds,
    }));
  };

  const handleConfirmOrderTypeChange = async () => {
    if (pendingOrderType !== null) {
      setFormValues(prev => ({
        ...prev,
        orderTypeId: pendingOrderType,
        selectedItems: [],
      }));

      setSelectAll(false);
      setIsLoading(true);
      setPage(0);
      setPageSize(10);
      setProductSearch('');

      await fetchItems({
        item: '',
        orderType: pendingOrderType,
        storeNumber: selectedStore?.storeNumber,
        categoryId: -1,
        supplier: '',
        pageNumber: 0,
        pageSize: 10,
        sort: 'Name',
        direction: 'asc',
      });
      
      setIsLoading(false);
    }
  
    setShowOrderTypeWarning(false);
    setPendingOrderType(null);
  };

  const handleCancelOrderTypeChange = () => {
    setShowOrderTypeWarning(false);
    setPendingOrderType(null);
  };
  
  const isSaveEnabled = (): boolean => {
    return formValues.name?.trim() !== '' 
    && formValues.orderTypeId !== -1 
    && (selectAll || formValues.selectedItems.length > 0)
    && isTemplateNameUnique(formValues.name);
  };

  const handleReasonSelectChange = (event: SyntheticEvent, value: IdNameModel[]) => setSelectedReasons(value);

  const isTemplateNameUnique = (name: string): boolean => {
    if (!name || !formValues.existingTemplates) return true;
    return !formValues.existingTemplates.some(
      existingName => existingName.trim().toLowerCase() === name.trim().toLowerCase(),
    );
  };

  const isDisabled = !hasWritePermissions || (formValues.status !== 1 && isEditMode);

  return (
    <PageArea>
      <Box>
        <CustomBreadCrumbs title={t('templatenav')}>
          <Link to="/manage-autoshipments/templates"
            component={RouterLink}>{ t('autoshipmentsnav') }</Link>
        </CustomBreadCrumbs>
      </Box>
      <Box display="flex"
        width="100%"
        height="100%"
        flexDirection="column" >
        <Grid container
          sx={{ pt: '6px', pb: '12px' }}
          spacing={4}>
          <Stack width='100%'
            direction={isMobile ? 'column' : 'row'}
            sx={{
              justifyContent: 'space-between',
            }}>
            <Box width={isMobile ? '100%' : 'auto'}>
              <PageTitle subTitle={!isEditMode ? t('templateSubheader') : ''}>
                { isEditMode ? t('templateDetails') : t('newTemplate') }
              </PageTitle>
            </Box>
            <Box>
              <Stack width='100%'
                spacing={5}
                direction={isMobile ? 'column' : 'row'}
                sx={{ pb: 2 }}>
                <Button 
                  variant="secondary" 
                  size='lg'
                  onClick={() => navigate('/manage-autoshipments/templates')}>
                  {t('cancelBtn')}
                </Button>
                {hasWritePermissions && (
                  <Button
                    variant="primary"
                    size="lg"
                    startIcon={<SvgIcon><Save01 /></SvgIcon>}
                    onClick={onSave}
                    disabled={!isSaveEnabled() || isDisabled}
                  >
                    {t('saveBtn')}
                  </Button>
                )}
              </Stack>
            </Box>
          </Stack>
          <Grid container>
            <Grid container
              spacing={5}
              mt={5}
              flexWrap={isMobile ? 'wrap' : 'nowrap'}
            >
              <Grid item
                xs={12}
                sm={6}
                md={3}
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
                <FormControl fullWidth
                  size='small'>
                  <TextField
                    label={t('templateName')}
                    size='small'
                    required
                    variant="outlined"
                    disabled={isDisabled}
                    value={formValues.name ?? ''}
                    onChange={handleFormChange('name')}
                    error={!isTemplateNameUnique(formValues.name)}
                  >
                  </TextField>
                  {!isTemplateNameUnique(formValues.name) && (
                    <Typography
                      variant='textXS'
                      color='error'
                      sx={{ mt: 0.5, display: 'block' }}
                    >
                      {t('templateNameExists')}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item
                xs={12}
                sm={6}
                md={3}
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
                <FormControl fullWidth
                  size='small'>
                  <TextField
                    fullWidth
                    autoComplete='off'
                    size='small'
                    label={t('orderType')}
                    disabled={isDisabled || formValues.hasPublishedSchedules}
                    value={formValues.orderTypeId}
                    required
                    onChange={(e) => {
                      const newOrderType = Number(e.target.value);
                      
                      // Check if there are selected items or selectAll is true
                      if (formValues.selectedItems.length > 0 || selectAll) {
                        setPendingOrderType(newOrderType);
                        setShowOrderTypeWarning(true);
                      } else {
                        setIsLoading(true);
                        handleFormChange('orderTypeId')(e);
                        fetchItems({
                          item: productSearch,
                          orderType: newOrderType,
                          storeNumber: selectedStore?.storeNumber,
                          categoryId: -1,
                          pageNumber: 0,
                          pageSize: pageSize,
                          sort: orderBy,
                          direction: order,
                          supplier: undefined,
                        });
                      }
                    }}
                    select
                  >
                    {orderTypes.map((type) => (
                      <MenuItem key={type.id}
                        value={type.id}>
                        {t(OrderTypeTranslationMap[type.name as keyof typeof OrderTypeTranslationMap]) || type.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>
              </Grid>
              <Grid item
                xs={12}
                sm={6}
                md={3}>
                <FormControl fullWidth>
                  <Autocomplete
                    fullWidth
                    multiple
                    limitTags={2}
                    options={autoshipReasons ?? []}
                    value={selectedReasons}
                    disabled={isDisabled}
                    onChange={handleReasonSelectChange}
                    getOptionKey={(option) => option.id ?? ''}
                    getOptionLabel={(option) => option.name ?? ''}
                    renderInput={(params) => (
                      <TextField {...params}
                        required={autoshipReasons.length === 0}
                        label={t('selectReasons')}
                        placeholder={t('selectReasons')} />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid >
      </Box >
      <Box> 
        <Grid item
          sx={{ pt: '1em' }}
          lg={6}
          xs={12}>
          {!isLoading &&
          <Stack width='100%'
            spacing={2}
            direction={isMobile ? 'column' : 'row'}
            sx={{
              justifyContent: 'space-between',
            }}>
            <Typography
              variant="displaySM"
              sx={{ fontWeight: 600 }}>{t('itemSelection')}</Typography>
            {!isDisabled && !formValues.hasPublishedSchedules && <Stack spacing={5}
              direction={'row'}
              sx={{ pb: '12px' }}>
              <Button
                variant="secondary"
                size="lg"
                startIcon={<SvgIcon><Download /></SvgIcon>}
                onClick={downloadItemsCsv}
              >
                {t('download')}
              </Button>
              <Button
                variant="secondary"
                size="lg"
                startIcon={<SvgIcon><Upload /></SvgIcon>}
                onClick={() => setOpenUploadItemsDialog(true)}
              >
                {t('upload')}
              </Button>
            </Stack>}
          </Stack>}
          {!isDisabled && !isLoading &&
          <Box sx = {{ mb: 5 }}> 
            <TextField
              size='small'
              placeholder={t('searchItems')}
              disabled={isLoading}
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SvgIcon><SearchSm /></SvgIcon>
                  </InputAdornment>
                ),
              }}
            />
          </Box>}

          {!isLoading && items.length > 0 && !isDisabled && !formValues.hasPublishedSchedules &&
          <FormGroup sx={{ pt: '10px' }}>
            <FormControlLabel
              control={<Checkbox checked={selectAll} 
                onChange={handleSelectAllChange} />}
              label={t('selectAll')} />
          </FormGroup>}

          {isLoading ? <LoadingWheel /> : <Section> 
            <TableContainer component={'div'}>
              <Table
                size="small"
                aria-label="a table"
              >
                <TableHead
                  sx={{
                    paddingTop: '10px',
                    backgroundColor: theme.palette.custom.gray[200],
                  }}>
                  <TableRow>
                    <TableCell
                      sx={{ fontWeight: 'bold' }}
                      align="left">
                      <TableSortLabel
                        direction={order}>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 'bold' }}
                      align="left">
                      <TableSortLabel
                        active={orderBy === 'SKU'}
                        direction={order}>
                        {t('itemNumber')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 'bold' }}
                      align="left">
                      <TableSortLabel
                        active={orderBy === 'Name'}
                        direction={order}>
                        {t('itemDescription')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 'bold' }}
                      align="left">
                      {t('uom')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items
                    .map((item) => {
                      return (
                        <TableRow key={item.id}>
                          <TableCell
                            size="small" 
                            style={{ display: 'flex', alignItems: 'center' }} >
                            <Checkbox
                              disabled={isDisabled || selectAll || formValues.hasPublishedSchedules}
                              color="primary"
                              checked={selectAll || formValues.selectedItems?.some((x) => x === item.id)}
                              onChange={(e) => handleItemChange(item.id!, e.target.checked)}
                            />
                          </TableCell>
                          <TableCell>{item.sku}</TableCell>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>{item.orderableUom}</TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
              {!isLoading && items.length === 0 ? (
                <Box
                  sx={{ py: '20px', px: '16px', textAlign: 'center' }}
                >
                  <Typography
                    variant="textLG"
                    sx={{ textAlign: 'center' }}
                  >
                    {t('noItems')}
                  </Typography>
                </Box>
              ) : (
                <CustomPagination page={page} 
                  setPage={setPage}
                  maxPages={totalPages}
                  breakpointForChangeDisplay={120000}
                />
              )}
            </TableContainer>
          </Section>
          }
        </Grid>
      </Box>
      <AutoshipUploadFileModal<Item>
        open={openUploadItemsDialog}
        onClose={() => handleCloseDialog()}
        onFileSelected={validateItems}
        onConfirm={onConfirmBulkUpload}
        columns={[
          { header: t('itemNumber'), accessor: (item) => item.sku },
          { header: t('itemDescription'), accessor: (item) => item.name },
          { header: t('uom'), accessor: (item) => item.primaryUom },
        ]}
        invalidItemRenderer={(item) => `${item.sku ?? ''}, ${item.name ?? ''}, ${item.primaryUom ?? ''}`}
        errorMessage={uploadErrorMessage}
      />

      <ActionConfirmationDialog
        open={showOrderTypeWarning}
        icon={<WarningAmberIcon />}
        variant="warning"
        title={t('warning')}
        message={t('orderTypeChangeWarning')}
        confirmButtonText={t('continue')}
        cancelButtonText={t('cancel')}
        onConfirm={handleConfirmOrderTypeChange}
        onCancel={handleCancelOrderTypeChange}
      />
      <MessagesSnackbar 
        open={snackbar.open}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        message={snackbar.message}
        severity={snackbar.severity}
        duration={5000} 
      />
    </PageArea >
  );
};

export default CreateOrEditAutoshipmentTemplate;